import React from 'react';

import { Add as AddIcon } from '@mui/icons-material';
import { Box, Grid, Typography } from '@mui/material';
import { FormSpy } from 'react-final-form';

import { validateWithYupSchema } from '@hrlogics/react-form-utils/yup';

import {
  EmployersTableListDocument,
  useSendInvitesToEmployersAdminsByCvAdminMutation,
} from 'src/api';
import { Button, FieldArrayTemplate } from 'src/components/common';
import { CheckboxField, Field, Form, TextField } from 'src/components/finalForm';
import { useNotification } from 'src/hooks/app';

import { EMPLOYER_INITIAL_VALUES, INITIAL_VALUES } from './constants';
import { InviteFormEmployerFields, InviteFormFields, InviteFormValues } from './types';
import { inviteFormValidationSchema } from './utils';

interface InviteEmployersAdminsByCVAdminModalFormProps {
  closeModal: () => void;
  switchClosability: (isClosureAllowed: boolean) => void;
}

export const InviteEmployersAdminsByCVAdminModalForm: React.FC<
  InviteEmployersAdminsByCVAdminModalFormProps
> = ({ closeModal, switchClosability }) => {
  const { createApolloContext } = useNotification();

  const [sendInvites] = useSendInvitesToEmployersAdminsByCvAdminMutation({
    refetchQueries: [EmployersTableListDocument],
    context: createApolloContext({ successMessage: 'Invitations sent' }),
  });

  const onSubmit = React.useCallback(
    async (formValues: InviteFormValues) => {
      switchClosability(false);

      try {
        await sendInvites({
          variables: {
            employers: formValues.employers,
          },
        });

        switchClosability(true);
        closeModal();
      } catch {
        switchClosability(true);
      }
    },
    [closeModal, sendInvites, switchClosability],
  );

  return (
    <Box>
      <Typography color="GrayText" variant="body1">
        Invite Employers by using their email address. If you want to invite Employer under PEO, you
        can make it via bulk-upload in PEO profile, Employers section.
      </Typography>
      <Form<InviteFormValues>
        onSubmit={onSubmit}
        subscription={{ valid: true, values: true, submitting: true }}
        containerProps={{ mt: 4 }}
        initialValues={INITIAL_VALUES}
        validate={validateWithYupSchema(inviteFormValidationSchema)}
      >
        {({ submitting, valid }) => {
          return (
            <React.Fragment>
              <Grid container spacing={2}>
                <Grid size={12}>
                  <FieldArrayTemplate
                    label="Invitation"
                    name={InviteFormFields.employers}
                    addIcon={<AddIcon />}
                    onAdd={fields => fields.push(EMPLOYER_INITIAL_VALUES)}
                    onRemove={(fields, index) => fields.remove(index)}
                  >
                    {({ renderFieldName, getArrayFieldState }) => (
                      <Grid container spacing={2}>
                        <Grid size={12}>
                          <Field
                            name={renderFieldName(InviteFormEmployerFields.email)}
                            label="Email Address"
                            placeholder="Type Email Address"
                            required
                            component={TextField}
                            fullWidth
                            disabled={submitting}
                          />
                        </Grid>
                        <Grid size={12}>
                          <FormSpy<InviteFormValues> subscription={{ values: true }}>
                            {({ values }) => {
                              const checkboxState = getArrayFieldState(
                                InviteFormEmployerFields.shouldCreateBillingRules,
                              );

                              if (
                                checkboxState?.touched &&
                                (!!values.peoPayrollProvider || !!values.peo)
                              ) {
                                checkboxState.change(false);
                              }

                              return (
                                <Field
                                  name={renderFieldName(
                                    InviteFormEmployerFields.shouldCreateBillingRules,
                                  )}
                                  label="Enable Revenue Share for this Employer"
                                  component={CheckboxField}
                                  fullWidth
                                  disabled={
                                    submitting || !!values.peoPayrollProvider || !!values.peo
                                  }
                                />
                              );
                            }}
                          </FormSpy>
                        </Grid>
                      </Grid>
                    )}
                  </FieldArrayTemplate>
                </Grid>
              </Grid>
              <Box display="flex" gap={2} mt={4} justifyContent="center">
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => closeModal()}
                  disabled={submitting}
                >
                  Close
                </Button>

                <Button
                  type="submit"
                  variant="contained"
                  disabled={!valid || submitting}
                  loading={submitting}
                >
                  Send invite
                </Button>
              </Box>
            </React.Fragment>
          );
        }}
      </Form>
    </Box>
  );
};
